/**
 * shapefileWriter — spec-level ESRI shapefile + dBASE III writers, promoted
 * from the parser test suite into a real export surface.
 *
 * One shapefile holds one shape type, so a bundle emits up to three sidecar
 * sets inside a single zip: `<stem>-polygons`, `<stem>-lines`,
 * `<stem>-points`, each with .shp/.shx/.dbf/.prj (WGS84). Attribute columns:
 * NAME / KIND / CATEGORY / AREA_AC / SOURCE. Coordinates are written in
 * EPSG:4326 (x = lng, y = lat) — round-trippable through our own
 * `parseShapefileZip`.
 */
import JSZip from 'jszip';

import type { LayoutExportBundle, LayoutExportFeature } from './layoutExportBundle';

export const WGS84_PRJ_WKT =
  'GEOGCS["GCS_WGS_1984",DATUM["D_WGS_1984",SPHEROID["WGS_1984",6378137.0,298.257223563]],'
  + 'PRIMEM["Greenwich",0.0],UNIT["Degree",0.0174532925199433]]';

type XY = readonly [number, number];

const SHAPE_TYPE = { point: 1, line: 3, polygon: 5 } as const;

function signedRingArea2D(ring: readonly XY[]): number {
  let sum = 0;
  for (let i = 0; i < ring.length - 1; i++) {
    const a = ring[i]!;
    const b = ring[i + 1]!;
    sum += a[0] * b[1] - b[0] * a[1];
  }
  return sum / 2;
}

function closeRingXY(points: ReadonlyArray<{ lat: number; lng: number }>): XY[] {
  const ring = points
    .filter(point => Number.isFinite(point.lat) && Number.isFinite(point.lng))
    .map(point => [point.lng, point.lat] as const);
  if (ring.length < 3) return [...ring];
  const first = ring[0]!;
  const last = ring.at(-1)!;
  return first[0] === last[0] && first[1] === last[1] ? [...ring] : [...ring, first];
}

function orientClosedRing(ring: readonly XY[], clockwise: boolean): XY[] {
  if (ring.length < 4) return [...ring];
  const isCw = signedRingArea2D(ring) < 0;
  return isCw === clockwise ? [...ring] : [...ring].reverse();
}

function featureXY(feature: LayoutExportFeature): XY[] {
  if (feature.geometry.type !== 'polygon') {
    return feature.geometry.coordinates
      .filter(point => Number.isFinite(point.lat) && Number.isFinite(point.lng))
      .map(point => [point.lng, point.lat] as const);
  }
  return closeRingXY(feature.geometry.coordinates);
}

function featurePartsXY(feature: LayoutExportFeature): XY[][] {
  const outer = featureXY(feature);
  if (feature.geometry.type !== 'polygon') return [outer];
  const holes = (feature.geometry.holes ?? [])
    .map(hole => closeRingXY(hole))
    .filter(hole => hole.length >= 4 && distinctPointCount(hole) >= 3);
  if (holes.length === 0) return [outer];
  return [
    orientClosedRing(outer, true),
    ...holes.map(hole => orientClosedRing(hole, false)),
  ];
}

function distinctPointCount(points: readonly XY[]): number {
  return new Set(points.map(([x, y]) => `${x},${y}`)).size;
}

interface ShpRecord {
  shapeType: number;
  points: XY[];
  partOffsets?: readonly number[];
}

function recordContentBytes(record: ShpRecord): number {
  if (record.shapeType === SHAPE_TYPE.point) return 20;
  const numParts = record.partOffsets?.length ?? 1;
  return 44 + 4 * numParts + 16 * record.points.length;
}

/** Write a .shp of ONE shape type; returns null when no records qualify. */
export function writeShp(records: readonly ShpRecord[]): ArrayBuffer | null {
  if (records.length === 0) return null;
  const contentBytes = records.map(recordContentBytes);
  const totalBytes = 100 + contentBytes.reduce((sum, bytes) => sum + 8 + bytes, 0);
  const buf = new ArrayBuffer(totalBytes);
  const view = new DataView(buf);

  const allPoints = records.flatMap(record => record.points);
  const xs = allPoints.map(point => point[0]);
  const ys = allPoints.map(point => point[1]);
  const shapeType = records[0]!.shapeType;

  view.setInt32(0, 9994, false);
  view.setInt32(24, totalBytes / 2, false);
  view.setInt32(28, 1000, true);
  view.setInt32(32, shapeType, true);
  view.setFloat64(36, Math.min(...xs), true);
  view.setFloat64(44, Math.min(...ys), true);
  view.setFloat64(52, Math.max(...xs), true);
  view.setFloat64(60, Math.max(...ys), true);

  let offset = 100;
  records.forEach((record, index) => {
    const bytes = contentBytes[index]!;
    view.setInt32(offset, index + 1, false);
    view.setInt32(offset + 4, bytes / 2, false);
    let p = offset + 8;
    view.setInt32(p, record.shapeType, true); p += 4;
    if (record.shapeType === SHAPE_TYPE.point) {
      const [x, y] = record.points[0]!;
      view.setFloat64(p, x, true); p += 8;
      view.setFloat64(p, y, true); p += 8;
    } else {
      const rx = record.points.map(point => point[0]);
      const ry = record.points.map(point => point[1]);
      view.setFloat64(p, Math.min(...rx), true); p += 8;
      view.setFloat64(p, Math.min(...ry), true); p += 8;
      view.setFloat64(p, Math.max(...rx), true); p += 8;
      view.setFloat64(p, Math.max(...ry), true); p += 8;
      const partOffsets = record.partOffsets ?? [0];
      view.setInt32(p, partOffsets.length, true); p += 4;
      view.setInt32(p, record.points.length, true); p += 4;
      for (const partOffset of partOffsets) {
        view.setInt32(p, partOffset, true); p += 4;
      }
      for (const [x, y] of record.points) {
        view.setFloat64(p, x, true); p += 8;
        view.setFloat64(p, y, true); p += 8;
      }
    }
    offset += 8 + bytes;
  });

  return buf;
}

/** Companion .shx index (fixed 8-byte records mirroring the .shp layout). */
export function writeShx(records: readonly ShpRecord[]): ArrayBuffer {
  const totalBytes = 100 + records.length * 8;
  const buf = new ArrayBuffer(totalBytes);
  const view = new DataView(buf);
  view.setInt32(0, 9994, false);
  view.setInt32(24, totalBytes / 2, false);
  view.setInt32(28, 1000, true);
  view.setInt32(32, records[0]?.shapeType ?? 0, true);
  let shpOffsetWords = 50;
  records.forEach((record, index) => {
    const contentWords = recordContentBytes(record) / 2;
    view.setInt32(100 + index * 8, shpOffsetWords, false);
    view.setInt32(100 + index * 8 + 4, contentWords, false);
    shpOffsetWords += 4 + contentWords;
  });
  return buf;
}

export interface DbfField {
  name: string;
  length: number;
}

/** dBASE III with character fields only (screening metadata is textual). */
export function writeDbf(
  fields: readonly DbfField[],
  rows: ReadonlyArray<readonly string[]>,
): ArrayBuffer {
  const headerSize = 32 + 32 * fields.length + 1;
  const recordSize = 1 + fields.reduce((sum, field) => sum + field.length, 0);
  const totalBytes = headerSize + recordSize * rows.length + 1;
  const buf = new ArrayBuffer(totalBytes);
  const view = new DataView(buf);
  const bytes = new Uint8Array(buf);

  view.setUint8(0, 0x03);
  view.setUint8(1, 126); view.setUint8(2, 7); view.setUint8(3, 22);
  view.setUint32(4, rows.length, true);
  view.setUint16(8, headerSize, true);
  view.setUint16(10, recordSize, true);

  fields.forEach((field, fi) => {
    const base = 32 + 32 * fi;
    for (let i = 0; i < Math.min(field.name.length, 10); i++) {
      view.setUint8(base + i, field.name.charCodeAt(i));
    }
    view.setUint8(base + 11, 'C'.charCodeAt(0));
    view.setUint8(base + 16, field.length);
  });
  view.setUint8(headerSize - 1, 0x0d);

  rows.forEach((row, ri) => {
    const base = headerSize + ri * recordSize;
    view.setUint8(base, 0x20);
    let cursor = base + 1;
    fields.forEach((field, fi) => {
      const padded = String(row[fi] ?? '').padEnd(field.length, ' ').slice(0, field.length);
      for (let i = 0; i < field.length; i++) {
        // Non-ASCII collapses to '?' — dBASE III has no encoding negotiation.
        const code = padded.charCodeAt(i);
        bytes[cursor + i] = code < 0x20 || code > 0x7e ? 0x3f : code;
      }
      cursor += field.length;
    });
  });
  view.setUint8(totalBytes - 1, 0x1a);

  return buf;
}

const DBF_FIELDS: readonly DbfField[] = [
  { name: 'FEATURE_ID', length: 64 },
  { name: 'NAME', length: 64 },
  { name: 'KIND', length: 32 },
  { name: 'CATEGORY', length: 32 },
  { name: 'AREA_AC', length: 16 },
  { name: 'SOURCE', length: 32 },
];

function dbfRow(feature: LayoutExportFeature): string[] {
  return [
    feature.id,
    feature.name,
    feature.kind,
    feature.category,
    feature.areaAcres == null ? '' : feature.areaAcres.toFixed(3),
    feature.source,
  ];
}

export interface ExportedShapefile {
  blob: Blob;
  filename: string;
  /** Sidecar sets written into the zip (by geometry type). */
  parts: Array<'polygons' | 'lines' | 'points'>;
}

/** Bundle → one zip holding a sidecar set per populated geometry type. */
export async function exportBundleAsShapefile(
  bundle: LayoutExportBundle,
): Promise<ExportedShapefile> {
  const zip = new JSZip();
  const parts: ExportedShapefile['parts'] = [];

  const groups: ReadonlyArray<readonly ['polygons' | 'lines' | 'points', LayoutExportFeature[]]> = [
    ['polygons', bundle.features.filter(feature => feature.geometry.type === 'polygon')],
    ['lines', bundle.features.filter(feature => feature.geometry.type === 'line')],
    ['points', bundle.features.filter(feature => feature.geometry.type === 'point')],
  ];

  for (const [part, features] of groups) {
    const qualified = features
      .map(feature => {
        const partsXY = featurePartsXY(feature);
        const outer = partsXY[0] ?? [];
        const partOffsets: number[] = [];
        const allPts: XY[] = [];
        for (const ring of partsXY) {
          partOffsets.push(allPts.length);
          allPts.push(...ring);
        }
        return { feature, outer, points: allPts, partOffsets };
      })
      .filter(({ feature, outer }) => (
        feature.geometry.type === 'point'
          ? outer.length >= 1
          : feature.geometry.type === 'line'
            ? outer.length >= 2 && distinctPointCount(outer) >= 2
            : outer.length >= 4 && distinctPointCount(outer) >= 3
      ));
    if (qualified.length === 0) continue;
    const records: ShpRecord[] = qualified.map(({ feature, points, partOffsets }) => ({
      shapeType: SHAPE_TYPE[feature.geometry.type],
      points: feature.geometry.type === 'point' ? [points[0]!] : points,
      ...(feature.geometry.type === 'polygon' && partOffsets.length > 1 ? { partOffsets } : {}),
    }));
    const shp = writeShp(records);
    if (!shp) continue;
    const stem = `${bundle.name}-${part}`;
    zip.file(`${stem}.shp`, shp);
    zip.file(`${stem}.shx`, writeShx(records));
    zip.file(`${stem}.dbf`, writeDbf(DBF_FIELDS, qualified.map(({ feature }) => dbfRow(feature))));
    zip.file(`${stem}.prj`, WGS84_PRJ_WKT);
    parts.push(part);
  }

  if (parts.length === 0) {
    throw new Error('Nothing to export: the bundle has no drawable features.');
  }

  zip.file('manifest.json', JSON.stringify(bundle, null, 2));
  const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
  return { blob, filename: `${bundle.name}-shapefile.zip`, parts };
}
